import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ChangeEvent, JSX, useState } from "react";
import { Typography } from "@platform/design-system";

type MuiAutocompleteProps<
  T,
  D extends boolean,
  M extends boolean,
> = AutocompleteProps<T, D, M, false>;

type Props<T, D extends boolean, M extends boolean> = Omit<
  MuiAutocompleteProps<T, D, M>,
  "renderInput"
> & {
  label?: string;
  placeholder?: string;
  minChartsBeforeSearch?: number;
  renderInput?: MuiAutocompleteProps<T, D, M>["renderInput"];
};

function AutocompleteTextField<T, D extends boolean, M extends boolean>({
  label,
  placeholder,
  filterOptions,
  minChartsBeforeSearch = 3,
  ...props
}: Props<T, D, M>): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  const onTextSearched = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newTerm = e.target.value || "";
    if (newTerm.length >= minChartsBeforeSearch) {
      setIsOpen(true);
    }
  };

  return (
    <>
      <MuiAutocomplete
        clearOnBlur
        clearOnEscape
        blurOnSelect
        onBlur={() => setIsOpen(false)}
        open={isOpen}
        sx={{
          ".MuiInputLabel-root:not(.Mui-focused)": {
            color: "text.secondary",
          },
          ".MuiOutlinedInput-root.MuiInputBase-root": {
            paddingRight: "16px",
          },
        }}
        filterOptions={(options: T[], state) => {
          if (state.inputValue.length < minChartsBeforeSearch) {
            return [];
          }
          return filterOptions ? filterOptions(options, state) : options;
        }}
        renderInput={(params) => (
          <TextField
            onChange={onTextSearched}
            {...params}
            label={label}
            placeholder={placeholder}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: <SearchIcon />,
                endAdornment: null,
                autoComplete: "off",
              },
            }}
          />
        )}
        {...props}
      />
      {minChartsBeforeSearch > 0 ? (
        <Typography variant="caption" color="textSecondary">
          Search with at least {minChartsBeforeSearch} letters
        </Typography>
      ) : null}
    </>
  );
}

export default AutocompleteTextField;
